import { useQuery } from 'react-query';
import { useImmer } from 'use-immer';
import Scrollbars from 'react-custom-scrollbars';
import type { CategoryData } from '@types';
import { fetchTourData } from '@apis/tourApis';
import cn from '@utils/cn';
import IconArrow from '@components/icons/IconArrow';
import { CheckboxNoFunc } from '@components/Checkbox';
import Icon from '@components/Icon';
import { BlockLoading } from '@components/BlockLoading';
import { useScenesCopyContext } from './scenesCopyContext';
import ImgNew from './img-new.png';

export function TargetList() {
  const {
    targetTid,
    level,
    targetCategoryId,
    isNewCategory = false,
    setScenesCopyState,
  } = useScenesCopyContext();
  const [expanded, setExpanded] = useImmer(new Set());

  const { data: tourData, isLoading } = useQuery(
    ['sc-target', targetTid, level],
    async () => {
      if (!targetTid) return;
      return fetchTourData(targetTid);
    },
    {
      onSuccess: (_tourData) => {
        if (!_tourData) return;
        if (level === '场景') {
          setExpanded((draft) => {
            _tourData.category.forEach((item) => {
              draft.add(item.id);
            });
          });
        }
        setScenesCopyState({ targetTourData: _tourData });
      },
    }
  );
  const data = tourData?.category ?? [];

  const renderSubItem = (item: CategoryData) => {
    const clickAvailable = level === '场景';
    return (
      <div
        key={item.id}
        className={cn(
          'color-[#909090] relative flex h-40 items-center bg-[#f8f8f8] pl-45 pr-10 hover:bg-[#efefef]'
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (!clickAvailable) return;
          setScenesCopyState({ targetCategoryId: item.id });
        }}
      >
        <div title={item.name} className="grow truncate">
          {item.name}
        </div>
        {clickAvailable && (
          <CheckboxNoFunc
            checked={targetCategoryId === item.id && !isNewCategory}
          />
        )}
      </div>
    );
  };

  const renderItem = (item: CategoryData) => {
    const hasSubCategory = (item.children ?? []).length > 0;
    const clickAvailable =
      (level === '二级分组' && hasSubCategory) ||
      (level === '场景' && !hasSubCategory);
    return (
      <div
        key={item.id}
        className="cursor-pointer overflow-hidden rounded"
        onClick={(e) => {
          e.stopPropagation();
          if (!clickAvailable) return;
          setScenesCopyState({ targetCategoryId: item.id });
        }}
      >
        <div
          className={cn(
            'flex h-40 items-center rounded px-10 hover:bg-[#efefef]',
            {
              'bg-[#efefef]': expanded.has(item.id),
            }
          )}
        >
          <IconArrow
            scheme="light"
            active
            className={cn('shrink-0', {
              'rotate-180': !expanded.has(item.id),
            })}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((draft) => {
                if (expanded.has(item.id)) {
                  draft.delete(item.id);
                } else {
                  draft.add(item.id);
                }
              });
            }}
          />
          <div className="mx-5 grow truncate" title={item.name}>
            {item.name}
          </div>
          {clickAvailable && (
            <CheckboxNoFunc
              checked={targetCategoryId === item.id && !isNewCategory}
            />
          )}
        </div>
        {expanded.has(item.id) && item.children?.map(renderSubItem)}
        {expanded.has(item.id) && level === '场景' && hasSubCategory && (
          <div
            className="color-[#909090] relative flex h-40 items-center bg-[#f8f8f8] pl-45 pr-10 hover:bg-[#efefef]"
            onClick={(e) => {
              e.stopPropagation();
              setScenesCopyState({
                targetCategoryId: item.id,
                isNewCategory: true,
              });
            }}
          >
            <Icon src={ImgNew} />
            <div className="mx-5 grow">作为新的二级分组</div>
            <CheckboxNoFunc
              checked={targetCategoryId === item.id && isNewCategory}
            />
          </div>
        )}
      </div>
    );
  };

  const renderText = () => {
    if (!targetTid) return '请在左侧选择一个要复制到的作品';
    if (level === '作品') return '复制整个作品时，无需选择分组';
    if (level === '一级分组') return '复制整个一级分组时，无需选择分组';
    if (level === '二级分组') return '请选择要复制到的一级分组';
    return '请选择要复制到的分组';
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="relative">
          <BlockLoading />
        </div>
      );
    }
    if (!targetTid) {
      return (
        <div className="pt flex flex-col items-center pt-30 text-black-gray">
          暂无内容
        </div>
      );
    }
    if (level === '作品') {
      return (
        <div className="pt flex flex-col items-center px-20 pt-30 text-orange">
          复制整个作品时，会把所有分组与场景一同复制进指定作品中。
        </div>
      );
    }
    if (level === '一级分组') {
      return (
        <div className="pt flex flex-col items-center px-20 pt-30 text-orange">
          复制整个一级分组时，会把其下全部的二级分组和场景一同复制进指定作品中。
        </div>
      );
    }
    return (
      <Scrollbars>
        {data.map(renderItem)}
        <div
          className="flex h-40 cursor-pointer items-center rounded px-10 hover:bg-[#efefef]"
          onClick={(e) => {
            e.stopPropagation();
            setScenesCopyState({ targetCategoryId: 0, isNewCategory: true });
          }}
        >
          <Icon src={ImgNew} />
          <div className="mx-5 grow">作为新的一级分组</div>
          <CheckboxNoFunc checked={targetCategoryId === 0 && isNewCategory} />
        </div>
      </Scrollbars>
    );
  };

  return (
    <div className="grid grid-rows-[50px_1fr] border-l pl-10">
      <div className="flex h-50 items-center bg-white-light px-20">
        {renderText()}
      </div>
      {renderContent()}
    </div>
  );
}

export default TargetList;
